import TableTooltip from '@/components/common/tooltip/table-tooltip/TableTooltip';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import useGetPaginatedEquipments from '@/hooks/queries/equipments-api/useGetPaginatedEquipments';
import usePersistedPaginationFiltersAndSorting from '@/hooks/usePersistedPaginationFiltersAndSorting';
import { TEquipment } from '@/services/equipments/equipments.types';
import { getFeatureFlagValue } from '@/utils/utils';
import {
  ColumnDef,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import { debounce } from 'lodash';
import { EditIcon, MoreHorizontal } from 'lucide-react';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const useInventoryTable = () => {
  const { t } = useTranslation();

  const {
    pagination,
    setPagination,
    filtering,
    setFiltering,
    sorting,
    setSorting,
    columnFilters,
    setColumnFilters
  } = usePersistedPaginationFiltersAndSorting({
    defaultSorting: [{ id: 'equipmentType.name', desc: false }]
  });

  const {
    data: dataInventory,
    isLoading,
    isError,
    isFetching
  } = useGetPaginatedEquipments({
    pagination,
    filtering,
    sorting,
    columnFilters
  });

  const [openModal, setOpenModal] = useState(false);
  const [equipmentData, setEquipmentData] = useState<TEquipment>();

  const featureFlagCrudValue = getFeatureFlagValue('crud');
  const inventory = dataInventory?.results;
  const emptyData = !inventory?.length;
  const [search, setSearch] = useState(filtering || '');

  const debounceFn = useCallback(
    debounce((value) => {
      setFiltering(value);
    }, 750),
    []
  );

  const onSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    debounceFn(event.target.value);
  };

  const columns = useMemo<ColumnDef<TEquipment>[]>(
    () => [
      {
        id: 'id',
        accessorKey: 'id',
        header: t('tables.common.id'),
        cell: ({ row }) => {
          const id = row?.original?.id;
          return <TableTooltip text={id} />;
        }
      },
      {
        id: 'code',
        accessorKey: 'code',
        header: t('tables.inventory.columns.code')
      },

      {
        id: 'name',
        accessorKey: 'name',
        header: t('tables.inventory.columns.name')
      },
      {
        id: 'description',
        accessorKey: 'description',
        header: t('tables.inventory.columns.description')
      },
      {
        id: 'equipmentType.name',
        accessorKey: 'equipmentType.name',
        header: t('tables.inventory.columns.category')
      },
      {
        id: 'sports',
        accessorKey: 'sports',
        accessorFn: ({ sports }) => {
          const stringSport = sports.map((sport) => sport.name).join(', ');
          return stringSport;
        },
        header: t('tables.inventoryCategory.serviceCategories'),
        enableSorting: false
      },

      {
        id: 'button',
        enableHiding: false,
        cell: ({ row }) => {
          const handleAvailability = () => {
            setEquipmentData(row.original);
            setOpenModal(true);
          };

          return (
            <>
              <Button className="hidden lg:block" onClick={handleAvailability}>
                {t('tables.inventory.columns.buttons.availability')}
              </Button>
              <div className="lg:hidden">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost">
                      <MoreHorizontal className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={handleAvailability}>
                      {t('tables.inventory.columns.buttons.availability')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </>
          );
        }
      },
      {
        id: 'actions-edit',
        enableHiding: false,
        cell: ({ row }) => {
          const inventoryId = row.original.id;
          if (!featureFlagCrudValue) {
            return undefined;
          }
          return (
            <Link to={`./edit/${inventoryId}`} className="w-full">
              <EditIcon />
            </Link>
          );
        }
      }
    ],

    [t, featureFlagCrudValue]
  );

  const table = useReactTable({
    data: inventory ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    //pagination config
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    rowCount: dataInventory?.total,
    pageCount: Math.ceil(
      (dataInventory?.total ?? 0) / (dataInventory?.limit ?? 1)
    ),
    manualPagination: true,
    //sort config
    enableMultiSort: false, // disable multi-sorting for the entire table
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    //filter config
    onColumnFiltersChange: setColumnFilters,
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFilteredRowModel: getFilteredRowModel(),
    onGlobalFilterChange: setFiltering,
    initialState: {
      columnVisibility: {
        id: false
      }
    },
    state: {
      sorting,
      globalFilter: filtering,
      pagination
    }
  });
  return {
    table,
    equipmentData,
    emptyData,
    search,
    onSearch,
    featureFlagCrudValue,
    isError,
    isFetching,
    isLoading,
    openModal,
    setOpenModal
  };
};

export default useInventoryTable;
